import { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTvData } from '../hooks/useTvData'
import { useListingsShowSignedProgramsFlag } from '@/hooks/useListingsShowSignedProgramsFlag'
import { useAuth } from '@/contexts/AuthContext'
import { USER_FAVOURITE_CHANNELS_QUERY_KEY } from '@/components/FavouriteChannels'
import { supabase } from '@/lib/supabase'
import { TvProgramme, TvChannel } from '../types/tvTypes'
import { buildProgrammeIdentifier } from '@/utils/programmeIdentifier'
import { useThreadForProgramme, type ThreadForProgramme } from '@/hooks/useThreadForProgramme'
import AddToExistingChatModal from '@/components/AddToExistingChatModal'
import AddCommentModal from '@/components/AddCommentModal'
import NewCommentFromTvModal from '@/components/NewCommentFromTvModal'
import TvListingImage from '@/components/TvListingImage'

function formatTime(date: Date): string {
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getDateKey(date: Date): string {
  // YYYY-MM-DD (UTC)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Next N days (UTC) for the date picker: [{ key: 'YYYY-MM-DD', label: 'Today' | 'Tomorrow' | 'Wed 5 Feb' }, ...]. N = listingDays (global setting: 7 or 14). */
function getListingsDayOptions(days: number): { key: string; label: string }[] {
  const opts: { key: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < days; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + i, 0, 0, 0, 0))
    const key = getDateKey(d)
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    opts.push({ key, label })
  }
  return opts
}

/** Build day options from a sorted list of date keys (e.g. when EPG data is for a range that doesn't include today). */
function getDayOptionsFromDates(dateKeys: string[]): { key: string; label: string }[] {
  const todayKey = getDateKey(new Date())
  return dateKeys.map((key) => {
    const d = new Date(key + 'T00:00:00Z')
    const label = key === todayKey ? 'Today' : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    return { key, label }
  })
}

/** Order channels so the user's favourites appear first (in favourite order), then the rest in original order. */
function orderChannelsWithFavouritesFirst(channels: TvChannel[], favouriteIds: string[]): TvChannel[] {
  if (favouriteIds.length === 0) return channels
  const byId = new Map(channels.map((c) => [c.id, c]))
  const ordered: TvChannel[] = []
  const seen = new Set<string>()
  for (const id of favouriteIds) {
    const ch = byId.get(id)
    if (ch && !seen.has(ch.id)) {
      seen.add(ch.id)
      ordered.push(ch)
    }
  }
  for (const ch of channels) {
    if (!seen.has(ch.id)) ordered.push(ch)
  }
  return ordered
}

export default function TvGuidePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, listingDays, loading, error } = useTvData()
  const { showSignedInListings } = useListingsShowSignedProgramsFlag()

  const { data: favouriteRows } = useQuery({
    queryKey: [...USER_FAVOURITE_CHANNELS_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data: res, error: err } = await supabase
        .from('user_favourite_channels')
        .select('channel_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
      if (err) return []
      return res ?? []
    },
    enabled: !!user?.id,
  })
  const favouriteChannelIds = useMemo(() => favouriteRows?.map((r) => r.channel_id) ?? [], [favouriteRows])

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    null
  )
  
  // Default to today (UTC); date picker shows next N days or, when EPG data is for a different range (e.g. future), the dates that have data
  const listingsDayOptions = useMemo(() => getListingsDayOptions(listingDays), [listingDays])
  const [selectedDate, setSelectedDate] = useState<string>(() => getDateKey(new Date()))

  // TV Guide toggles: filter by AD / Subtitles / BSL (Phase 6)
  const [adFilterOn, setAdFilterOn] = useState(false)
  const [subtitlesFilterOn, setSubtitlesFilterOn] = useState(false)
  const [bslFilterOn, setBslFilterOn] = useState(false)

  // Phase 8.3 / Direct comment: Discuss flow
  const [programmeForDiscuss, setProgrammeForDiscuss] = useState<TvProgramme | null>(null)
  // Direct comment: when existing thread found, "Add to existing?" → Yes opens AddCommentModal
  const [existingThreadForComment, setExistingThreadForComment] = useState<ThreadForProgramme | null>(null)
  // User chose "No, create New" → show group picker / create thread (interim) instead
  const [useCreateNewFromTv, setUseCreateNewFromTv] = useState(false)

  const programmeIdentifier = programmeForDiscuss ? buildProgrammeIdentifier(programmeForDiscuss) : null
  const programmeChannelId = programmeForDiscuss?.channelId ?? null
  const { data: threadForProgrammeData, isLoading: isLoadingThreadForProgramme } = useThreadForProgramme(
    programmeIdentifier,
    programmeChannelId
  )
  
  // Convert selected date string to Date and get date key
  const selectedDateKey = useMemo(() => {
    const date = new Date(selectedDate + 'T00:00:00Z')
    return getDateKey(date)
  }, [selectedDate])

  const programmesByChannelByDay = useMemo(() => {
    if (!data) return {} as Record<string, Record<string, TvProgramme[]>>
    const result: Record<string, Record<string, TvProgramme[]>> = {}

    for (const p of data.programmes) {
      const channelId = p.channelId
      const dayKey = getDateKey(p.start)

      if (!result[channelId]) {
        result[channelId] = {}
      }
      if (!result[channelId][dayKey]) {
        result[channelId][dayKey] = []
      }
      result[channelId][dayKey].push(p)
    }

    // Sort programmes per channel/day by start time
    Object.values(result).forEach((byDay) => {
      Object.values(byDay).forEach((list) => {
        list.sort((a, b) => a.start.getTime() - b.start.getTime())
      })
    })

    return result
  }, [data])

  const allChannels = useMemo(
    () => orderChannelsWithFavouritesFirst(data?.channels ?? [], favouriteChannelIds),
    [data?.channels, favouriteChannelIds]
  )

  // Accessibility filter: when any toggle is on, show only channels that have at least one matching programme for the selected date. BSL filter only applies when signed-in-listings flag is on.
  const hasAccessibilityFilter = adFilterOn || subtitlesFilterOn || (showSignedInListings && bslFilterOn)
  const programmeMatchesFilter = useCallback(
    (p: TvProgramme) =>
      (adFilterOn && p.has_ad) ||
      (subtitlesFilterOn && p.has_subtitles) ||
      (showSignedInListings && bslFilterOn && p.has_bsl),
    [adFilterOn, subtitlesFilterOn, showSignedInListings, bslFilterOn]
  )

  const channels = useMemo(() => {
    if (!data || !hasAccessibilityFilter) return allChannels
    const channelIdsWithMatches = new Set<string>()
    for (const p of data.programmes) {
      if (getDateKey(p.start) !== selectedDateKey) continue
      if (programmeMatchesFilter(p)) channelIdsWithMatches.add(p.channelId)
    }
    return allChannels.filter((c) => channelIdsWithMatches.has(c.id))
  }, [data, allChannels, hasAccessibilityFilter, selectedDateKey, programmeMatchesFilter])

  // Debug logging
  useEffect(() => {
    if (data) {
      console.log('TV Data loaded:', {
        channelsCount: data.channels.length,
        programmesCount: data.programmes.length,
        firstChannel: data.channels[0],
        firstProgramme: data.programmes[0],
      })
    }
  }, [data])

  const selectedChannel: TvChannel | undefined = useMemo(() => {
    if (!channels.length) return undefined
    const preferredId = selectedChannelId ?? channels[0]?.id
    return channels.find((c) => c.id === preferredId) ?? channels[0]
  }, [channels, selectedChannelId])

  // If the selected/default channel has no programmes for the selected day but other channels do,
  // auto-select the first channel (already favourite-first ordered) that has programmes.
  useEffect(() => {
    if (!channels.length) return

    const channelHasListingsForSelectedDay = (channelId: string) =>
      (programmesByChannelByDay[channelId]?.[selectedDateKey]?.length ?? 0) > 0

    const currentId = selectedChannelId ?? channels[0]?.id
    if (!currentId) return

    const anyListingsForSelectedDay = channels.some((c) => channelHasListingsForSelectedDay(c.id))
    if (!anyListingsForSelectedDay) return

    if (channelHasListingsForSelectedDay(currentId)) return

    const firstWithListings = channels.find((c) => channelHasListingsForSelectedDay(c.id))
    if (!firstWithListings) return
    if (firstWithListings.id === selectedChannelId) return

    setSelectedChannelId(firstWithListings.id)
  }, [channels, programmesByChannelByDay, selectedDateKey, selectedChannelId])

  const selectedProgrammes: TvProgramme[] = useMemo(() => {
    if (!selectedChannel) return []
    const byDay = programmesByChannelByDay[selectedChannel.id]
    if (!byDay) return []
    const programmes = byDay[selectedDateKey] ?? []
    if (!hasAccessibilityFilter) return programmes
    return programmes.filter(programmeMatchesFilter)
  }, [selectedChannel, programmesByChannelByDay, selectedDateKey, hasAccessibilityFilter, programmeMatchesFilter])
  
  // Get available dates from the data for the date picker
  const availableDates = useMemo(() => {
    if (!data) return []
    const dateSet = new Set<string>()
    data.programmes.forEach((p) => {
      const dateKey = getDateKey(p.start)
      dateSet.add(dateKey)
    })
    return Array.from(dateSet).sort()
  }, [data])

  // When EPG data is for a range that doesn't include today (e.g. Mar 15–19 while today is Feb), use those dates for the picker
  const effectiveDayOptions = useMemo(() => {
    if (availableDates.length === 0) return listingsDayOptions
    const todayInData = listingsDayOptions.some((o) => availableDates.includes(o.key))
    if (todayInData) return listingsDayOptions
    return getDayOptionsFromDates(availableDates)
  }, [listingsDayOptions, availableDates])

  // Clamp selected date to the effective day picker range (e.g. after midnight or when EPG range is different from today)
  useEffect(() => {
    const validKeys = effectiveDayOptions.map((o) => o.key)
    if (validKeys.length > 0 && !validKeys.includes(selectedDate)) {
      setSelectedDate(validKeys[0])
    }
  }, [effectiveDayOptions, selectedDate])

  const handleChannelClick = (channelId: string) => {
    setSelectedChannelId(channelId)
  }

  const closeDiscussFlow = () => {
    setProgrammeForDiscuss(null)
    setExistingThreadForComment(null)
    setUseCreateNewFromTv(false)
  }

  const showAddToExistingModal =
    programmeForDiscuss !== null &&
    threadForProgrammeData !== null &&
    !useCreateNewFromTv &&
    existingThreadForComment === null

  const showNewCommentFromTv =
    programmeForDiscuss !== null &&
    (useCreateNewFromTv || (threadForProgrammeData === null && !isLoadingThreadForProgramme)) &&
    existingThreadForComment === null

  const showAddCommentModal = existingThreadForComment !== null

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <p style={{ color: 'var(--text-primary)' }}>Loading TV listings...</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <p style={{ color: 'var(--text-primary)' }}>
          Sorry, we couldn&apos;t load the TV listings.
        </p>
        {error && (
          <p style={{ color: 'var(--color-error)' }}>Error: {error}</p>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto" aria-label="TV listings">
      {/* Toggles: filter by AD / Subtitles / BSL; when on, only channels with matching programmes for selected date are shown */}
      <div
        className="flex flex-wrap items-center justify-center gap-3 mb-4 px-1"
        role="group"
        aria-label="Accessibility filters"
      >
        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
          Accessibility Filters:
        </span>
        <button
          type="button"
          onClick={() => setAdFilterOn((on) => !on)}
          className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
          style={{
            backgroundColor: adFilterOn ? 'var(--bg-interactive)' : 'var(--bg-secondary)',
            color: adFilterOn ? 'var(--text-inverse)' : 'var(--text-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
          aria-pressed={adFilterOn}
          aria-label="Filter by AD (audio description)"
        >
          AD
        </button>
        {showSignedInListings && (
          <button
            type="button"
            onClick={() => setBslFilterOn((on) => !on)}
            className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
            style={{
              backgroundColor: bslFilterOn ? 'var(--bg-interactive)' : 'var(--bg-secondary)',
              color: bslFilterOn ? 'var(--text-inverse)' : 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-pressed={bslFilterOn}
            aria-label="Filter by BSL (British Sign Language)"
          >
            BSL
          </button>
        )}
        {/* Subtitles filter temporarily hidden — future functionality */}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: '1rem',
          minHeight: 'calc(100vh - 200px)',
        }}
      >
      {/* Channel list */}
      <aside
        style={{
          borderRight: '2px solid var(--bg-tertiary)',
          overflowY: 'auto',
          padding: '1rem',
          backgroundColor: 'var(--bg-secondary)',
        }}
        aria-label="Channel list"
      >
        <h2
          style={{
            fontSize: '1.25rem',
            marginBottom: '0.75rem',
            color: 'var(--text-primary)',
          }}
        >
          Channels ({channels.length})
        </h2>
        <p
          style={{
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
            marginBottom: '0.75rem',
          }}
          role="note"
        >
          If a channel is missing, contact the platform administrator.
        </p>
        {channels.length === 0 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            No channels loaded. Check console for errors.
          </p>
        )}
        {channels.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="channel-selector"
              className="block mb-2"
              style={{
                fontSize: '0.9rem',
                color: 'var(--text-primary)',
                fontWeight: 'semibold',
              }}
            >
              Select Channel:
            </label>
            <select
              id="channel-selector"
              value={selectedChannel?.id ?? channels[0]?.id ?? ''}
              onChange={(e) => handleChannelClick(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: '2px solid var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {channels.map((channel) => {
            const isActive =
              (selectedChannel?.id ?? channels[0]?.id) === channel.id

            return (
              <li key={channel.id} style={{ marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => handleChannelClick(channel.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    borderRadius: '0.5rem',
                    border: '2px solid var(--bg-tertiary)',
                    padding: '0.4rem 0.6rem',
                    backgroundColor: 'var(--bg-primary)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    gap: '0.5rem',
                    color: 'var(--text-primary)',
                  }}
                  aria-pressed={isActive}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-primary)'
                  }}
                >
                  {/* Channel logo or InsideAccess fallback */}
                  <TvListingImage
                    src={channel.icon}
                    alt=""
                    width={32}
                    height={32}
                    objectFit="contain"
                  />
                  {/* Channel name: highlight only when selected */}
                  <span
                    style={{
                      fontSize: '0.95rem',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '0.25rem',
                      backgroundColor: isActive
                        ? 'var(--bg-interactive)'
                        : 'transparent',
                      color: isActive
                        ? 'var(--text-inverse)'
                        : 'var(--text-primary)',
                    }}
                  >
                    {channel.name}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </aside>

      {/* Programme list */}
      <main
        style={{
          overflowY: 'auto',
          padding: '1rem',
        }}
      >
        <header
          style={{
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <TvListingImage
              src={selectedChannel?.icon}
              alt=""
              width={48}
              height={48}
              objectFit="contain"
            />
            <div>
              <h1
                style={{
                  fontSize: '1.5rem',
                  margin: 0,
                  color: 'var(--text-primary)',
                }}
              >
                {selectedChannel?.name ?? 'Channel'}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)',
                }}
              >
                Schedule for {new Date(selectedDate + 'T00:00:00Z').toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} (UTC)
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label
              htmlFor="tv-date-selector"
              style={{
                fontSize: '0.9rem',
                color: 'var(--text-primary)',
                fontWeight: 'semibold',
              }}
            >
              Date:
            </label>
            <select
              id="tv-date-selector"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              aria-label={effectiveDayOptions.length === listingsDayOptions.length ? `Select day (next ${listingDays} days)` : 'Select day'}
              style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '0.5rem',
                border: '2px solid var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                minWidth: '10rem',
              }}
            >
              {effectiveDayOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        {selectedProgrammes.length === 0 ? (
          <div style={{ color: 'var(--text-primary)' }}>
            <p>
              {hasAccessibilityFilter
                ? 'Sorry, there are no programmes that match those accessibility filters. Please try a different date.'
                : 'No programmes scheduled for the selected date.'}
            </p>
            {availableDates.length > 0 && !hasAccessibilityFilter && (
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Available dates: {availableDates.join(', ')}
              </p>
            )}
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              width: '100%',
            }}
          >
            {selectedProgrammes.map((programme, index) => (
              <li
                key={`${programme.channelId}-${programme.start.toISOString()}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <article
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: '0.75rem',
                    border: '2px solid var(--bg-tertiary)',
                    padding: '0.75rem',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: '0.75rem',
                    backgroundColor: 'var(--bg-secondary)',
                    alignItems: 'start',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
                    {/* Title row: programme title + accessibility icons in a row */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <h2
                        style={{
                          fontSize: '1rem',
                          margin: 0,
                          color: 'var(--text-primary)',
                        }}
                      >
                        {programme.title}
                      </h2>
                      {(programme.has_ad || programme.has_subtitles || (showSignedInListings && programme.has_bsl)) && (
                        <span
                          className="flex flex-wrap items-center gap-1.5"
                          style={{ flexShrink: 0 }}
                          aria-label={
                            [
                              programme.has_ad && 'AD',
                              programme.has_subtitles && 'Subtitles',
                              showSignedInListings && programme.has_bsl && 'BSL',
                            ]
                              .filter(Boolean)
                              .join(', ') || undefined
                          }
                        >
                          {programme.has_ad && (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                              title="Audio description available"
                            >
                              AD
                            </span>
                          )}
                          {programme.has_subtitles && (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                              title="Subtitles available"
                            >
                              Subtitles
                            </span>
                          )}
                          {showSignedInListings && programme.has_bsl && (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                              title="British Sign Language available"
                            >
                              BSL
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {formatTime(programme.start)} – {formatTime(programme.stop)}
                    </p>
                    {programme.onscreenEpisode && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        Episode: {programme.onscreenEpisode}
                      </p>
                    )}
                    {programme.desc && (
                      <p
                        style={{
                          marginTop: '0.4rem',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                          color: 'var(--text-primary)',
                        }}
                      >
                        {programme.desc}
                      </p>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <TvListingImage
                      src={programme.icon}
                      alt={programme.title}
                      width={90}
                      height={130}
                      objectFit="cover"
                      style={{ borderRadius: '0.5rem' }}
                    />
                  </div>
                </article>
                {user && (
                  <button
                    type="button"
                    onClick={() => setProgrammeForDiscuss(programme)}
                    aria-label={`Start a discussion about ${programme.title}`}
                    className="px-3 py-2 text-sm font-medium rounded-lg border shrink-0"
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = '2px solid var(--color-focus)'
                      e.currentTarget.style.outlineOffset = '2px'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.outline = ''
                      e.currentTarget.style.outlineOffset = ''
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    Discuss
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
      </div>

      {programmeForDiscuss && isLoadingThreadForProgramme && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          role="status"
          aria-live="polite"
          aria-label="Checking for existing thread"
        >
          <p className="rounded-lg px-4 py-3" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
            Checking for existing thread…
          </p>
        </div>
      )}

      {showAddToExistingModal && threadForProgrammeData && (
        <AddToExistingChatModal
          isOpen={true}
          onClose={closeDiscussFlow}
          onYes={() => setExistingThreadForComment(threadForProgrammeData)}
          onNoCreateNew={() => setUseCreateNewFromTv(true)}
          existing={threadForProgrammeData}
        />
      )}

      {showAddCommentModal && existingThreadForComment && user && (
        <AddCommentModal
          threadId={existingThreadForComment.thread.id}
          onClose={closeDiscussFlow}
          onSuccess={() => {
            closeDiscussFlow()
            navigate(`/groups/${existingThreadForComment.group.id}`, {
              state: { selectedThreadId: existingThreadForComment.thread.id },
            })
          }}
        />
      )}

      {showNewCommentFromTv && user && programmeForDiscuss && (
        <NewCommentFromTvModal
          isOpen={true}
          onClose={closeDiscussFlow}
          onSuccess={(groupId, threadId) => {
            closeDiscussFlow()
            navigate(`/groups/${groupId}`, { state: { selectedThreadId: threadId } })
          }}
          programme={programmeForDiscuss}
          channelName={channels.find((c) => c.id === programmeForDiscuss.channelId)?.name}
          prefillThread={!useCreateNewFromTv}
        />
      )}
    </div>
  )
}
